// src/components/Footer.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

// Re-export so other components that import DRAuraLogo from Footer still work
export const DRAuraLogo = ({ size = 40 }) => (
  <img
    src="/dr-logo-icon.png"
    alt="DR-Aura"
    width={size}
    height={size}
    style={{ objectFit: 'contain', filter: 'drop-shadow(0 0 6px rgba(100,120,255,0.4))' }}
  />
);

const Modal = ({ title, children, onClose }) => (
  <div className="modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
    <div className="modal-box" style={{ maxHeight: '80vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem' }}>{title}</h2>
        <button onClick={onClose} className="btn btn-ghost btn-sm" style={{ fontSize: '1.2rem' }}>✕</button>
      </div>
      <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.7 }}>
        {children}
      </div>
    </div>
  </div>
);

const PrivacyContent = () => (
  <>
    <p><strong style={{color:'var(--text-primary)'}}>Last updated:</strong> February 2026</p>
    <br />
    <p><strong style={{color:'var(--text-primary)'}}>1. Information We Collect</strong><br />
    We collect information you provide directly (name, email, recipe uploads) and automatically (usage analytics, device info). Firebase Authentication handles login securely.</p>
    <br />
    <p><strong style={{color:'var(--text-primary)'}}>2. How We Use Your Information</strong><br />
    To provide and improve DR-Aura services, personalize recipe recommendations, process PayPal subscription payments, and send service-related emails.</p>
    <br />
    <p><strong style={{color:'var(--text-primary)'}}>3. Data Sharing</strong><br />
    We do not sell your personal data. We share data only with Firebase (Google), PayPal (payments), and analytics providers under strict data processing agreements.</p>
    <br />
    <p><strong style={{color:'var(--text-primary)'}}>4. Cookies</strong><br />
    We use essential cookies for authentication and preference storage. Analytics cookies are optional and can be opted out via browser settings.</p>
    <br />
    <p><strong style={{color:'var(--text-primary)'}}>5. Your Rights</strong><br />
    You may request access, correction, or deletion of your personal data at privacy@dr-aura.online. EU users have GDPR rights including data portability.</p>
    <br />
    <p><strong style={{color:'var(--text-primary)'}}>6. Data Retention</strong><br />
    Account data is retained while your account is active. Recipe uploads are retained until you delete them. Analytics data is anonymized after 26 months.</p>
    <br />
    <p><strong style={{color:'var(--text-primary)'}}>7. Security</strong><br />
    We use industry-standard encryption, Firebase Security Rules, and regular security audits to protect your data.</p>
    <br />
    <p><strong style={{color:'var(--text-primary)'}}>8. Contact</strong><br />
    privacy@dr-aura.online | DR-Aura Smart Recipe Finder</p>
  </>
);

const TermsContent = () => (
  <>
    <p><strong style={{color:'var(--text-primary)'}}>Last updated:</strong> February 2026</p>
    <br />
    <p><strong style={{color:'var(--text-primary)'}}>1. Acceptance</strong><br />
    By using DR-Aura, you agree to these Terms. If you do not agree, please do not use the service.</p>
    <br />
    <p><strong style={{color:'var(--text-primary)'}}>2. User Accounts</strong><br />
    You are responsible for maintaining account security. You must be 13+ to use DR-Aura. One account per person.</p>
    <br />
    <p><strong style={{color:'var(--text-primary)'}}>3. Pro Subscriptions</strong><br />
    Pro plans are billed monthly via PayPal. Subscriptions auto-renew. Cancel anytime. No refunds for partial months. Plan features are subject to change with 30 days notice.</p>
    <br />
    <p><strong style={{color:'var(--text-primary)'}}>4. Recipe Uploads & Earnings</strong><br />
    By uploading recipes, you grant DR-Aura a non-exclusive license to display your content. Earnings from popular recipes are at DR-Aura's sole discretion. Spam, plagiarism, or infringing content will be removed and accounts suspended.</p>
    <br />
    <p><strong style={{color:'var(--text-primary)'}}>5. Prohibited Conduct</strong><br />
    No spamming, scraping, reverse engineering, or attempting to circumvent security measures. No uploading of harmful, illegal, or infringing content.</p>
    <br />
    <p><strong style={{color:'var(--text-primary)'}}>6. Limitation of Liability</strong><br />
    DR-Aura is provided "as is." We are not liable for nutritional accuracy, allergic reactions, or cooking outcomes. Always verify ingredients for dietary restrictions.</p>
    <br />
    <p><strong style={{color:'var(--text-primary)'}}>7. Contact</strong><br />
    legal@dr-aura.online</p>
  </>
);

const Footer = () => {
  const [modal, setModal] = useState(null); // 'privacy' | 'terms' | null

  return (
    <>
      <footer style={{
        background: 'var(--dark-1)',
        borderTop: '1px solid var(--border)',
        padding: '1.5rem 0 1rem',
      }}>
        <div className="container">
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: 16,
          }}>
            {/* Logo + tagline */}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <img
                src="/DR-Aura SRF 1.png"
                alt="DR-Aura Smart Recipe Finder"
                style={{
                  height: 40,
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 0 6px rgba(100,120,255,0.35))',
                }}
              />
            </div>

            {/* Links */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
              {[
                { label: 'Home', to: '/' },
                { label: 'Pricing', to: '/pricing' },
                { label: 'Community', to: '/community' },
                { label: 'About', to: '/about' },
              ].map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  style={{ fontFamily: 'var(--font-ui)', fontSize: '0.82rem', color: 'var(--text-muted)', textDecoration: 'none', transition: 'color 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'var(--text-secondary)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                >
                  {link.label}
                </Link>
              ))}
              <button onClick={() => setModal('privacy')} style={{ fontFamily: 'var(--font-ui)', fontSize: '0.82rem', color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', transition: 'color 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--text-secondary)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
              >Privacy Policy</button>
              <button onClick={() => setModal('terms')} style={{ fontFamily: 'var(--font-ui)', fontSize: '0.82rem', color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', transition: 'color 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--text-secondary)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
              >Terms of Service</button>
            </div>

            {/* Copyright */}
            <div style={{ fontFamily: 'var(--font-ui)', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              © {new Date().getFullYear()} DR-Aura. All rights reserved.
            </div>
          </div>
        </div>
      </footer>

      {modal === 'privacy' && (
        <Modal title="Privacy Policy" onClose={() => setModal(null)}>
          <PrivacyContent />
        </Modal>
      )}
      {modal === 'terms' && (
        <Modal title="Terms of Service" onClose={() => setModal(null)}>
          <TermsContent />
        </Modal>
      )}
    </>
  );
};

export default Footer;
